import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, OnInit, signal } from '@angular/core';
import { CommonButtonRowWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonUrlField, CommonUrlFieldConfig, CommonUrlFieldWidget } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { WebLink } from '@interfaces';
import { DomainDataService } from '../../../services';
import { WebLinkService } from './web-links.service';


@Component({
	selector: 'weblinks-part',
	standalone: true,
	imports: [
		NgClass,
		NgTemplateOutlet,
		CommonButtonRowWidget,
		CommonSelectFieldWidget,
		CommonUrlFieldWidget
	],
	templateUrl: './web-links.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './web-links.part.scss'
})

export class WebLinksPart implements OnInit {

	private domainService = inject(DomainDataService);
	private dialogService = inject(DialogService);
	private webLinkService = inject(WebLinkService);

	public personId = input.required<number>();
	public personName = input.required<string>();

	public readonly form = new CommonForm(inject(Injector));
	showForm = signal<boolean>(false);
	values = signal<{ webLinkId: number, url: string, webLinkTypeId: number }>({ webLinkId: 0, url: '', webLinkTypeId: 0 })

	readonly webLinks = computed(() => this.webLinkService.webLinks().map(e => ({ ...e, icon: this.getLinkIcon(e.webLinkTypeId) })));

	async ngOnInit() {
		await this.webLinkService.getWebLinks(this.personId());
	}

	saveOrUpdateWebLink() {
		// maintaining this approach as the same view utilizes a shared form for both adding and editing.
		if (this.form.allValidWithoutError()) {
			const values = this.values();
			if (values.webLinkId) {
				this.webLinkService.updateWebLink(this.personId(), { webLinkId: values.webLinkId, url: values.url, webLinkTypeId: values.webLinkTypeId, deleted: false });
			} else {
				this.webLinkService.addWebLink(this.personId(), values.url, values.webLinkTypeId);
			}
			this.closeForm();
		} else {
			// force show the errors
			this.form.resetAll(true);
		}

	}

	showAddForm() {
		this.showForm.set(true);
		this.values.set({ webLinkId: 0, url: '', webLinkTypeId: 0 });
	}

	closeForm() {
		this.showForm.set(false);
		this.values.set({ webLinkId: 0, url: '', webLinkTypeId: 0 });
	}


	async confirmWebLinkDeletion(link: WebLink) {
		const yes = await this.dialogService.confirm(`Do you want to remove ${link.url} from ${this.personName()}?`, 'Yes', 'No', [], 400, 250);
		if (yes) {
			this.webLinkService.updateWebLink(this.personId(), { ...link, deleted: true });
			this.closeForm();
		}
	}

	editWebLink(webLinkId: number, webLinkTypeId: number, url: string) {
		this.showForm.set(true);
		this.values.set({ webLinkId, webLinkTypeId, url });
	}

	private getLinkIcon(webLinkTypeId: number | undefined): string {
		switch (webLinkTypeId) {
			case 1://Website
				return 'fa-regular fa-link';
			case 2://Facebook
				return 'fa-brands fa-facebook';
			case 3://LinkedIn
				return 'fa-brands fa-linkedin';
			case 4://Twitter
				return 'fa-brands fa-twitter';
			default:
				return 'fa-brands fa-link';
		}
	}

	private readonly linkTypeOptions = computed(() => {
		return this.domainService.data().webLinkTypes.map(type => ({ value: type.webLinkTypeId, text: type.name }));
	});

	readonly linkType = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Link Type',
			isRequired: true,
			options: this.linkTypeOptions(),
		}),
		formFieldName: 'linkTypeId',
		form: this.form,
		actualValue: computed(() => this.values().webLinkTypeId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			this.values.update(v => ({ ...v, webLinkTypeId: value }))
		},
		error: signal(''),
	}));

	readonly url = computed<CommonUrlField>(() => ({
		config: signal<CommonUrlFieldConfig>({
			label: 'URL',
			isRequired: true,
			max: 100,
		}),
		formFieldName: 'url',
		form: this.form,
		actualValue: computed(() => this.values().url),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			this.values.update(v => ({ ...v, url: value }))
		},
		error: signal(''),
	}));

}